<template>
  <v-row no-gutters>
    <v-col cols="12" v-if="isFetched">
      <card-title-row :straightTitle="additionalModuleName" class="py-5" />
      <v-row class="pt-1 pr-5 flex-nowrap" no-gutters align="center">
        <template v-if="isAssignedFeaturesLoading">
          <v-col>
            <main-loader />
          </v-col>
        </template>
        <template v-else-if="assignedFeature">
          <v-col cols="1"></v-col>
          <v-col cols="9">
            <v-row>
              <v-col cols="auto" class="pr-2 text-body-3 font-weight-medium">
                {{ $i18n.t('sidebar.zdmEditor.copyFrom') }}:
              </v-col>
              <v-col cols="auto" class="text-body-3 pr-2 font-weight-medium" :style="`color: ${$_colors.primary}`">
                {{ getFeatureDescAttributeValue() }}
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="pr-2">
                <v-chip-group style="z-index: 1">
                  <v-tooltip bottom color="font" v-for="(chip, index) in assignedFeature.chips" :key="index">
                    <template #activator="{ on }">
                      <v-chip
                        v-on="on"
                        :ripple="false"
                        class="caption"
                        x-small
                        :class="{
                          'white--text': !$_isHexColorLight(chip['color'] || 'primary'),
                          'font--text': $_isHexColorLight(chip['color'] || 'primary'),
                        }"
                        :color="chip.color || 'primary'"
                      >
                        {{ chip.text }}
                      </v-chip>
                    </template>
                    {{ chip.text }}
                  </v-tooltip>
                </v-chip-group>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="auto" class="ml-auto">
            <v-tooltip bottom color="font">
              <template #activator="{ on }">
                <v-icon v-on="on" @click="zoomToAssignedFeature(assignedFeature)" color="rgba(0,0,0,.7)" size="19px">
                  mdi-magnify
                </v-icon>
              </template>
              {{ $i18n.t('navbar.zoomIn') }}
            </v-tooltip>
          </v-col>
          <v-col cols="auto">
            <dots-menu
              :items="dotsItems"
              @delete="unassignFeature"
              @details="openAssignedFeaturePreview"
              :disabled="isLoading"
            />
          </v-col>
        </template>
        <template v-else>
          <v-col cols="1"></v-col>
          <v-col cols="auto" class="pr-4 text-body-3 font-weight-medium">
            {{ $i18n.t('sidebar.zdmEditor.copyFrom') }}:
          </v-col>
          <v-col>
            <data-input
              simple
              :data-type="{ name: 'features' }"
              @pushFeatures="assignFeature"
              :featuresSettings="copyFeaturesSettings"
              translationPath="sidebar.zdmEditor.selectFromMap"
              :disabled="isLoading"
              :additional-attributes="[numberAttributeName]"
            />
          </v-col>
          <v-col class="font-italic font-weight-light italic--text">
            {{ $_loweralize($i18n.t('default.or')) }}
          </v-col>
          <v-col>
            <data-input
              dense
              hide-details
              :label="numberAttributeVerboseName"
              :data-type="{ name: 'text' }"
              v-model="assignedFeatureInput"
              @keydown.enter="getFeatureToAssign()"
              :disabled="isLoading"
            />
          </v-col>
        </template>
      </v-row>
      <v-row no-gutters class="pt-5">
        <v-col cols="1"> </v-col>
        <v-col cols="auto" class="pr-1">
          <data-input
            ref="feature-to-add-input"
            :dataType="{ name: 'geometry' }"
            name="streetLampInventoryFeatureToAdd"
            button-component="baseRoundedButton"
            :buttonProps="{
              icon: 'mdi-plus',
              size: 'x-small',
              disableDefaultMargin: true,
            }"
            :geometry-type="this.getOlGeometryTypeFromLayerGeometry(mainLayer?.geometry_type)"
            snapping
            :tooltip="$i18n.t('sidebar.zdmEditor.addFeature')"
            v-model="newFeatureGeometry"
            :disabled="isLoading || isAssignedFeaturesLoading"
            disable-editing
          />
        </v-col>
        <v-col cols="auto">
          <data-input
            simple
            rounded
            v-bind="featureToDeleteInput"
            :featuresSettings="deleteFeaturesSettings"
            :tooltip="$i18n.t('sidebar.zdmEditor.deleteFeature')"
            @pushFeatures="handleAddFeatureToDelete"
          />
        </v-col>
      </v-row>

      <!-- Dodane -->
      <template v-if="session.insert?.length">
        <v-row no-gutters class="pt-2">
          <v-col cols="1"></v-col>
          <v-col cols="11">
            <card-title-row
              is-subtitle
              :has-icon-column="false"
              translation-path="sidebar.zdmEditor.objectsAdded"
              class="pt-2"
            />
          </v-col>
        </v-row>
        <v-row no-gutters class="justify-end pr-5">
          <v-col cols="1"></v-col>
          <v-col cols="auto">
            <clickable-text @click="deleteAllObjectsFromSession('insert')">
              {{ $i18n.t('sidebar.zdmEditor.clearAll') }}
            </clickable-text>
          </v-col>
        </v-row>
        <v-row no-gutters class="pb-1 pr-5">
          <v-col cols="12">
            <v-list class="py-0">
              <template
                v-for="(object, idx) in tabsListExpanded?.['insert']
                  ? session.insert
                  : session.insert.slice(0, MAX_ELEMENTS_ON_LISTS)"
              >
                <v-row no-gutters :key="idx" align="center">
                  <v-col cols="1"> </v-col>
                  <v-col cols="11">
                    <v-list-item class="list-element-insert px-0 py-1">
                      <v-list-item-content class="pa-0">
                        <v-list-item-title>
                          <v-row no-gutters style="align-items: center" class="px-3 pt-1">
                            <v-col cols="auto">
                              <v-subheader class="pl-0 font--text text-body-2" style="height: auto;">
                                {{ `${mainLayer.name}: ${getFeatureDescAttributeValue(object)}` }}
                              </v-subheader>
                            </v-col>
                          </v-row>
                          <v-row no-gutters class="px-3">
                            <v-col cols="auto">
                              <v-chip-group style="z-index: 1">
                                <v-tooltip
                                  bottom
                                  color="font"
                                  v-for="(chip, index) in getChipsFromTree(object)"
                                  :key="index"
                                >
                                  <template #activator="{ on }">
                                    <v-chip
                                      v-on="on"
                                      :ripple="false"
                                      class="caption"
                                      x-small
                                      :class="{
                                        'white--text': !$_isHexColorLight(chip['color'] || 'primary'),
                                        'font--text': $_isHexColorLight(chip['color'] || 'primary'),
                                      }"
                                      :color="chip.color || 'primary'"
                                    >
                                      {{ chip.text }}
                                    </v-chip>
                                  </template>
                                  {{ chip.text }}
                                </v-tooltip>
                              </v-chip-group>
                            </v-col>
                          </v-row>
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-row no-gutters align="center">
                          <v-col cols="auto">
                            <v-tooltip bottom color="font">
                              <template #activator="{ on }">
                                <v-icon v-on="on" @click="zoomToFeature(object)" color="rgba(0,0,0,.7)" size="19px">
                                  mdi-magnify
                                </v-icon>
                              </template>
                              {{ $i18n.t('navbar.zoomIn') }}
                            </v-tooltip>
                          </v-col>
                          <v-col cols="auto">
                            <dots-menu
                              :items="addedObjectsDotsItems"
                              @delete="deleteOneObjectFromSession(object)"
                              @edit="editObjectFromSession(object)"
                              @assignFeature="assignObjectFromSession(object)"
                              :disabled="isLoading"
                            />
                          </v-col>
                        </v-row>
                      </v-list-item-action>
                    </v-list-item>
                  </v-col>
                </v-row>
              </template>
            </v-list>
            <v-list-item
              v-if="session.insert.length > MAX_ELEMENTS_ON_LISTS"
              class="px-0"
              style="max-height: 30px; min-height: 0px"
            >
              <v-col cols="1" />
              <v-list-item-action class="ma-0">
                <link-underlined
                  @click="tabsListExpanded['insert'] = !tabsListExpanded['insert']"
                  :translation-path="tabsListExpanded['insert'] ? 'sidebar.showLess' : 'sidebar.showMore'"
                  :disabled="isLoading"
                />
              </v-list-item-action>
            </v-list-item>
          </v-col>
        </v-row>
      </template>

      <!-- Usunięte -->
      <template v-if="session.delete?.length">
        <v-row no-gutters class="pt-5">
          <v-col cols="1"></v-col>
          <v-col cols="11">
            <card-title-row
              is-subtitle
              :has-icon-column="false"
              translation-path="sidebar.zdmEditor.objectsDeleted"
              class="pt-2"
            />
          </v-col>
        </v-row>
        <v-row no-gutters class="justify-end pr-5">
          <v-col cols="1"></v-col>
          <v-col cols="auto">
            <clickable-text @click="deleteAllObjectsFromSession('delete')">
              {{ $i18n.t('sidebar.zdmEditor.clearAll') }}
            </clickable-text>
          </v-col>
        </v-row>
        <v-row no-gutters class="pb-1 pr-5">
          <v-col cols="12">
            <v-list class="py-0">
              <template
                v-for="(object, idx) in tabsListExpanded?.['delete']
                  ? session.delete
                  : session.delete.slice(0, MAX_ELEMENTS_ON_LISTS)"
              >
                <v-row no-gutters :key="idx" align="center">
                  <v-col cols="1"> </v-col>
                  <v-col cols="11">
                    <v-list-item class="list-element-delete px-0 py-1">
                      <v-list-item-content class="pa-0">
                        <v-list-item-title>
                          <v-row no-gutters style="align-items: center" class="px-3 pt-1">
                            <v-col cols="auto">
                              <v-subheader class="pl-0 font--text text-body-2" style="height: auto;">
                                {{ `${mainLayer.name}: ${getFeatureDescAttributeValue(object)}` }}
                              </v-subheader>
                            </v-col>
                          </v-row>
                          <v-row no-gutters class="px-3">
                            <v-col cols="auto">
                              <v-chip-group style="z-index: 1">
                                <v-tooltip
                                  bottom
                                  color="font"
                                  v-for="(chip, index) in getChipsFromTree(object)"
                                  :key="index"
                                >
                                  <template #activator="{ on }">
                                    <v-chip
                                      v-on="on"
                                      :ripple="false"
                                      class="caption"
                                      x-small
                                      :class="{
                                        'white--text': !$_isHexColorLight(chip['color'] || 'primary'),
                                        'font--text': $_isHexColorLight(chip['color'] || 'primary'),
                                      }"
                                      :color="chip.color || 'primary'"
                                    >
                                      {{ chip.text }}
                                    </v-chip>
                                  </template>
                                  {{ chip.text }}
                                </v-tooltip>
                              </v-chip-group>
                            </v-col>
                          </v-row>
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-row no-gutters align="center">
                          <v-col cols="auto">
                            <v-tooltip bottom color="font">
                              <template #activator="{ on }">
                                <v-icon v-on="on" @click="zoomToFeature(object)" color="rgba(0,0,0,.7)" size="19px">
                                  mdi-magnify
                                </v-icon>
                              </template>
                              {{ $i18n.t('navbar.zoomIn') }}
                            </v-tooltip>
                          </v-col>
                          <v-col cols="auto">
                            <dots-menu :items="deletedObjectsDotsItems" @delete="deleteOneObjectFromSession(object)" />
                          </v-col>
                        </v-row>
                      </v-list-item-action>
                    </v-list-item>
                  </v-col>
                </v-row>
              </template>
            </v-list>
            <v-list-item
              v-if="session.delete.length > MAX_ELEMENTS_ON_LISTS"
              class="px-0"
              style="max-height: 30px; min-height: 0px"
            >
              <v-col cols="1" />
              <v-list-item-action class="ma-0">
                <link-underlined
                  @click="tabsListExpanded['delete'] = !tabsListExpanded['delete']"
                  :translation-path="tabsListExpanded['delete'] ? 'sidebar.showLess' : 'sidebar.showMore'"
                  :disabled="isLoading"
                />
              </v-list-item-action>
            </v-list-item>
          </v-col>
        </v-row>
      </template>

      <treeview-feature-dialog
        v-if="isFeatureDialogVisible"
        :is-visible.sync="isFeatureDialogVisible"
        :loading="isLoading"
        :treeview="treeview"
        v-model="currentFeature"
        :read-only="readOnly"
        @agree="agree"
      />
    </v-col>
    <main-loader v-else class="pt-7" />
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import ClickableText from '@/components/ClickableText';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import LinkUnderlined from '@/components/LinkUnderlined';
import MainLoader from '@/components/MainLoader';

import geometryTypeMapping from '@/mixins/map/geometryTypeMapping';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'StreetLampInventory',
  components: {
    CardTitleRow,
    ClickableText,
    DataInput,
    DotsMenu,
    MainLoader,
    LinkUnderlined,
    TreeviewFeatureDialog: () => import('@/components/TreeviewFeatureDialog'),
  },
  mixins: [geometryTypeMapping, prepareAttributesFeatureCardMixin],
  computed: {
    additionalModuleName: get('admin/additionalModules@ILS_SESSIONS_MODULE.verbose_name'),
    treeviews: get('attributesSchema/treeviews'),
    treeviewId: get('admin/modulesMapping@ils.settings.tree_view_id'),
    numberAttributeName: get('admin/modulesMapping@ils.settings.street_lamp_number_attribute'),
    treeview() {
      return this.treeviews.find(treeview => treeview.id === this.treeviewId);
    },
    flatTreeview() {
      return this.$_arrayToObject(this.$_flattenStructure([this.treeview], 'children', true), 'layer');
    },
    session: sync('ils_easy/session'),
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    mainLayerId() {
      return this.treeview?.layer;
    },
    mainLayer() {
      return this.layers[this.mainLayerId];
    },
    mainDataSourceName() {
      return this.mainLayer?.data_source_name;
    },
    mainDatasource() {
      return this.dataSources[this.mainDataSourceName];
    },

    mainUseQualifiedNames() {
      return this.mainDatasource?.attributes_use_datasource_qualified_names;
    },
    mainLayerAttributes() {
      return this.$_flattenStructure(
        this.getForm(
          this.mainLayer?.form_schema?.elements || [],
          this.mainDatasource?.attributes_schema?.attributes || []
        )
      );
    },
    mainIdAttributeName() {
      return this.mainDatasource?.attributes_schema.id_name;
    },
    numberAttributeVerboseName() {
      return this.mainLayerAttributes.find(attribute => attribute.attribute === this.numberAttributeName)?.label;
    },

    copyFeaturesSettings() {
      return {
        layers: {
          included: [this.mainLayerId, 'ils_street_lamp'],
        },
      };
    },
    featureToDeleteInput() {
      return {
        dataType: {
          name: 'features',
        },
        buttonProps: {
          icon: 'mdi-delete',
          size: 'x-small',
          color: 'rgba(225,225,225,1)',
          iconColor: 'rgba(0,0,0,0.7)',
        },
        disabled: this.isLoading,
      };
    },
    deleteFeaturesSettings() {
      return {
        layers: {
          included: [this.mainLayerId],
        },
      };
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: vm => ({
    isAssignedFeaturesLoading: false,
    isFetched: false,
    isLoading: false,
    mergeLoading: false,

    isFeatureDialogLoaded: false,
    isFeatureDialogVisible: false,
    currentFeature: {},
    newFeatureGeometry: undefined,
    readOnly: false,

    assignedFeature: undefined,
    assignedFeatureInput: null,

    dotsItems: [{ name: 'details' }, { name: 'delete' }],

    MAX_ELEMENTS_ON_LISTS: 5,
    addedObjectsDotsItems: [
      {
        name: 'edit',
      },
      {
        name: 'assignFeature',
        customName: vm.$i18n.t('menu.setAsTemplate'),
      },
      {
        name: 'delete',
        customName: vm.$i18n.t('menu.deleteFromList'),
      },
    ],
    deletedObjectsDotsItems: [
      {
        name: 'delete',
        customName: vm.$i18n.t('menu.deleteFromList'),
      },
    ],
    tabsListExpanded: { insert: false, delete: false },
  }),
  methods: {
    getTreeFeature: call('attributesSchema/getTreeFeature'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    getTreeviews: call('attributesSchema/getTreeviews'),

    getSession: call('ils_easy/getSession'),
    addSessionFeature: call('ils_easy/addSessionFeature'),
    editSessionFeature: call('ils_easy/editSessionFeature'),
    deleteObjectFromSession: call('ils_easy/deleteObjectFromSession'),
    deleteObjectsFromSession: call('ils_easy/deleteObjectsFromSession'),
    deleteObjectToSession: call('ils_easy/deleteObjectToSession'),
    mergeSession: call('ils_easy/mergeSession'),

    async getFeatureToAssign(value = this.assignedFeatureInput) {
      let formatedFeature;
      const featureInSession = this.session.insert.find(f => {
        return f.properties[this.numberAttributeName] === value;
      });
      if (featureInSession) {
        const { id, datasource: dataSource, number } = featureInSession.metadata;
        formatedFeature = {
          id,
          dataSource,
          layerId: 'ils_street_lamp',
          [this.numberAttributeName]: number,
        };
      } else {
        const payload = {
          layer_id: this.mainLayerId,
          features_filter: {
            $EQUAL: {
              [this.mainUseQualifiedNames
                ? this.numberAttributeName
                : `${this.mainDataSourceName}.${this.numberAttributeName}`]: {
                value,
              },
            },
          },
        };
        const r = await this.getLayerFeatures(payload);
        const feature = r.data.data.features.features[0];
        if (feature) {
          const { id, properties } = feature;
          const displayAttrbuteValue =
            this.mainIdAttributeName === this.numberAttributeName ? feature.id : properties[this.numberAttributeName];
          formatedFeature = {
            id,
            dataSource: this.mainDataSourceName,
            layerId: this.mainLayerId,
            [this.numberAttributeName]: displayAttrbuteValue,
          };
        }
      }
      if (formatedFeature) {
        this.assignFeature([formatedFeature]);
      } else {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.notFoundFeature'),
        });
      }
    },
    assignObjectFromSession(feature) {
      this.assignFeature([
        {
          id: feature.metadata.id,
          dataSource: this.mainDataSourceName,
          layerId: 'ils_street_lamp',
          [this.numberAttributeName]: feature.metadata.number,
        },
      ]);
    },
    async assignFeature(f) {
      if (!f?.length) return;
      let feature = f[0];
      this.isAssignedFeaturesLoading = true;
      try {
        let featureTree;
        if ('ils_street_lamp' === feature.layerId) {
          const featureInSession = this.session.insert.find(f => {
            return f.properties[this.numberAttributeName] === feature[this.numberAttributeName];
          });
          if (!featureInSession) return;
          const {
            metadata: { children },
            properties,
          } = featureInSession;
          featureTree = {
            children,
            id: feature.id,
            metadata: { datasource: feature.dataSource, layer: this.mainLayerId },
            properties,
          };
        } else if (feature.layerId === this.mainLayerId) {
          featureTree = await this.getTreeFeature({ layerId: feature.layerId, fid: feature.id });
        }
        feature = {
          ...featureTree,
          chips: this.getChipsFromTree(featureTree),
        };
      } finally {
        this.isAssignedFeaturesLoading = false;
      }
      this.assignedFeature = feature;
      this.assignedFeatureInput = '';
    },
    unassignFeature() {
      this.assignedFeature = undefined;
    },

    async handleAddFeatureToDelete(feature) {
      this.isLoading = true;
      try {
        if (feature.length) {
          const { layerId, id: featureId } = feature[0];
          await this.deleteObjectToSession({ layerId, featureId });
        }
      } finally {
        this.isLoading = false;
      }
    },

    getFeatureDescAttributeValue(feature = this.assignedFeature) {
      if (this.mainIdAttributeName === this.numberAttributeName) return feature.id;
      return feature.properties?.[this.numberAttributeName];
    },
    getChipsFromTree(tree) {
      const {
        properties,
        metadata: { badges = this.treeview.badges, datasource },
      } = tree;
      return badges
        ?.map(badge => {
          const { badge_color: color, badge_color_attribute: attribute } = badge;
          let { badge_text: text } = badge;
          text = this.$_getQueryValue(text, properties);

          if (attribute) {
            const badgeAttributeMetadata =
              this.dataSources[datasource]?.attributes_schema.attributes.find(attr => attr.id === attribute) || {};
            const { name, dict_colors: dictColors, allowed_values: dictValues } = badgeAttributeMetadata;
            const index = dictValues.indexOf(properties[name]);
            return { text, color: dictColors[index] };
          }
          return { text, color };
        })
        .filter(badge => !!badge.text);
    },
    openAssignedFeaturePreview() {
      this.currentFeature = this.assignedFeature;
      this.readOnly = true;
      this.isFeatureDialogLoaded = true;
      this.isFeatureDialogVisible = true;
    },
    zoomToFeature(feature) {
      const { geometry } = feature;
      this.$root.$emit('createGeomAndFitView', {
        ...geometry,
        crs: { type: 'name', properties: { name: this.$_config.defaultEpsg } },
      });
    },
    zoomToAssignedFeature() {
      const {
        metadata: { layer: layerId },
        id,
      } = this.assignedFeature;
      this.$root.$emit('zoomToFeature', layerId, layerId === 'ils_street_lamp' ? 'ils_street_lamp' : 'layers', id);
    },
    async init() {
      await Promise.all([this.getTreeviews(), this.getSession()]);
      this.$root.$off('agreeLampInventory');
      this.$root.$on('agreeLampInventory', this.agreeChanges);
      this.stickyButtons = [
        {
          action: 'agreeLampInventory',
          translationPath: 'sidebar.zdmEditor.makeChanges',
        },
      ];
      this.isStickyButtonsVisible = true;
      this.isFetched = true;
    },
    async agreeChanges() {
      this.mergeLoading = true;
      this.isLoading = true;
      try {
        await this.mergeSession();
        const layersIds = Object.keys(this.flatTreeview);
        this.$root.$emit(
          'refreshMvtSources',
          layersIds.map(layer => ({ id: layer, group: 'layers' })),
          true
        );
        this.$root.$emit('updateLegendStylesCounts', { layersIds });
      } finally {
        this.isLoading = false;
        this.mergeLoading = false;
      }
    },
    async agree() {
      this.isLoading = true;
      try {
        const payload = JSON.parse(JSON.stringify(this.currentFeature));
        if (!this.currentFeature.id) {
          payload.geometry = this.newFeatureGeometry;
          await this.addSessionFeature(payload);
          this.isFeatureDialogVisible = false;
        } else {
          const id = payload.id;
          delete payload.id;
          await this.editSessionFeature({ id, payload });
          this.isFeatureDialogVisible = false;
        }
      } finally {
        this.isLoading = false;
      }
    },
    async deleteOneObjectFromSession(feature) {
      this.isLoading = true;
      try {
        await this.deleteObjectFromSession(feature.metadata.id);
      } finally {
        this.isLoading = false;
      }
    },
    async editObjectFromSession(feature) {
      const mapEditElement = (element, level = 0) => {
        const {
          geometry,
          metadata: { children, id, datasource, layer = this.mainLayerId },
          properties,
        } = element;
        return {
          children: children?.map(child => mapEditElement(child, level + 1)) || null,
          ...(level === 0 ? { id } : {}),
          metadata: { datasource, layer },
          geometry: geometry ? { ...geometry, crs: { properties: { name: 'EPSG:3857' }, type: 'name' } } : geometry,
          properties,
        };
      };
      this.currentFeature = mapEditElement(JSON.parse(JSON.stringify(feature)));
      this.readOnly = false;
      this.isFeatureDialogLoaded = true;
      this.isFeatureDialogVisible = true;
    },
    async deleteAllObjectsFromSession(operation) {
      this.isLoading = true;
      try {
        await this.deleteObjectsFromSession(operation);
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {
    isFeatureDialogVisible(nV) {
      if (!nV) {
        const hadGeometry = JSON.parse(JSON.stringify(this.newFeatureGeometry || '')) ? true : false;
        this.newFeatureGeometry = null;
        this.currentFeature = {};
        this.$nextTick(() => {
          if (hadGeometry) {
            this.$refs['feature-to-add-input']?.$refs['data-input']?.toggleOn('drawGeometry');
          }
          this.isCopyPreview = false;
        });
      }
    },
    newFeatureGeometry(nV) {
      if (nV) {
        if (this.assignedFeature) {
          const formSchema = this.getForm(
            this.mainLayer.form_schema.elements,
            this.mainDatasource.attributes_schema.attributes,
            { filteredOutAttributes: [this.numberAttributeName] }
          );
          const mapCopyElement = (element, level = 0) => {
            const {
              children,
              metadata: { datasource, layer },
              geometry,
              properties,
            } = element;
            return {
              children: children?.map(child => mapCopyElement(child, level + 1)) || null,
              metadata: { datasource, layer },
              geometry,
              properties: level === 0 ? this.getValues(formSchema, properties, true) : {},
            };
          };
          this.currentFeature = mapCopyElement(this.assignedFeature);
        } else {
          this.currentFeature = {};
        }
        this.readOnly = false;
        this.isFeatureDialogLoaded = true;
        this.isFeatureDialogVisible = true;
      }
    },
  },
  mounted() {
    this.init();
    this.$watch(
      vm => {
        const { isLoading, isFetched, mergeLoading } = vm;
        return { isLoading, isFetched, mergeLoading };
      },
      nV => {
        const { isLoading, isFetched, mergeLoading } = nV;
        if (isFetched && !isLoading) {
          this.isStickyButtonsValid = true;
          this.isStickyButtonsLoading = mergeLoading;
        } else {
          this.isStickyButtonsValid = false;
          this.isStickyButtonsLoading = mergeLoading;
        }
      }
    );
  },
  beforeDestroy() {
    this.session = {};
    this.isStickyButtonsVisible = false;
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsValid = false;
  },
};
</script>
<style lang="scss" scoped>
.list-element-insert {
  @include border(all, 1px, rgba(map-get($colors, 'success'), 0.5));
  background-color: rgba(map-get($colors, 'success'), 0.1);
}
.list-element-insert:hover {
  background-color: rgba(map-get($colors, 'success'), 0.2);
}
.list-element-delete {
  @include border(all, 1px, rgba(map-get($colors, 'error'), 0.5));
  background-color: rgba(map-get($colors, 'error'), 0.1);
}
.list-element-delete:hover {
  background-color: rgba(map-get($colors, 'error'), 0.2);
}
</style>
